import { useCallback, useEffect, useRef, useState } from 'react';
import { Socket } from 'socket.io-client';
import { useLatestCallbackRef } from './useLatestCallbackRef';

export type Bubble = {
    id: number;
    count: number;
    minDeployBlockTime: number;
    twitterUsername: string | null;
};

export type Token = {
    mint: string;
    name: string;
    symbol: string;
    description: string | null;
    telegram: string | null;
    twitter: string | null;
    website: string | null;
    image: string | null;
    deployBlockTime: number;
    completeBlockTime: number | null;
};

export interface CommonUseBubblesProps {
    enabled: boolean;
    onNewBubble: () => void;
}

interface UseBubblesProps<B extends Bubble> extends CommonUseBubblesProps {
    getBubbles: () => Promise<B[]>;
    client?: Socket;
    updateEventName: string;
}

const removedBubbleIds: Set<number> = new Set();

export function useBubbles<B extends Bubble, Update extends { oldIds: Array<number>; current: B }>({
    enabled,
    getBubbles,
    client,
    updateEventName,
    onNewBubble,
}: UseBubblesProps<B>) {
    const [state, setState] = useState<{
        bubbles: B[];
        loading: boolean | null;
    }>({
        bubbles: [],
        loading: null,
    });

    const stateRef = useRef<{
        bubbles: B[];
        loading: boolean | null;
    }>({
        bubbles: [],
        loading: null,
    });

    const newBubbleHandlerRef = useLatestCallbackRef(onNewBubble);

    useEffect(() => {
        if (!enabled) return;

        const fetchData = async () => {
            try {
                setState({ bubbles: [], loading: true });

                const latest = await getBubbles();

                newBubbleHandlerRef.current();

                stateRef.current = { bubbles: latest, loading: false };
                setState(stateRef.current);
            } catch (error) {
                console.error('Failed to fetch bubbles', error);
            }
        };

        fetchData();
    }, [enabled, getBubbles, newBubbleHandlerRef]);

    useEffect(() => {
        if (!client) return;

        if (enabled) {
            client.on(updateEventName, async (event: Update) => {
                const stillExistingBubbles = stateRef.current.bubbles.filter((bubble) => !event.oldIds.includes(bubble.id));

                if (event.oldIds.some((id) => removedBubbleIds.has(id))) {
                    removedBubbleIds.add(event.current.id);
                    event.oldIds.forEach((id) => removedBubbleIds.delete(id));
                } else {
                    newBubbleHandlerRef.current();
                }

                stateRef.current = {
                    loading: false,
                    bubbles: removedBubbleIds.has(event.current.id) ? stillExistingBubbles : [event.current, ...stillExistingBubbles].slice(0, 20),
                };
            });
        }

        return () => {
            client.off(updateEventName);
        };
    }, [enabled, client, updateEventName, newBubbleHandlerRef]);

    useEffect(() => {
        let interval: ReturnType<typeof setInterval>;

        if (enabled) {
            interval = setInterval(() => {
                setState(stateRef.current);
            }, 1000);
        }

        return () => clearInterval(interval);
    }, [enabled]);

    const removeBubble = useCallback((id: number) => {
        stateRef.current = {
            loading: false,
            bubbles: stateRef.current.bubbles.filter((bubble) => bubble.id !== id),
        };

        setState(stateRef.current);
        removedBubbleIds.add(id);
    }, []);

    return [state, removeBubble] as const;
}
